import type { UserProfile } from '../../lib/api';
import { departmentLabel } from '../../lib/departments';
import { userStatusLabel } from './usersFilters';

/**
 * The table's data columns in the order the Users Management table shows them
 * (Invitation and Actions are controls, not data), then the export-only
 * columns (Approved, Created At).
 */
const HEADERS = [
  'Email', 'Name', 'Company', 'Department', 'Primary Contact For', 'User Role',
  'Last Login', 'Status', 'Approved', 'Created At',
];

function toRow(u: UserProfile): string[] {
  return [
    u.user.email,
    u.user.full_name,
    u.company_name,
    departmentLabel(u.department),
    (u.primary_contact_for ?? []).map((a) => a.company_name).join('; '),
    u.role_name,
    u.user.last_login ? new Date(u.user.last_login).toISOString() : '',
    // Same word the Status badge shows, so an "Invite Sent" filter exports
    // rows reading Invite Sent rather than Active.
    userStatusLabel(u),
    u.is_approved ? 'Yes' : 'No',
    new Date(u.created_at).toLocaleDateString(),
  ];
}

/** Quote a cell, doubling embedded quotes so a name like O"Brien can't split the row. */
const csvCell = (value: string | null | undefined): string => `"${(value ?? '').replace(/"/g, '""')}"`;

/**
 * Download the given users as a CSV file.
 *
 * Exports exactly what the table is showing — the caller passes the filtered,
 * sorted list (every page, not just the visible one), not the raw fetch.
 *
 * @param users - Users to export, already filtered and sorted for display.
 * @param today - ISO date used in the filename, defaults to now.
 */
export function exportUsersToCSV(users: UserProfile[], today = new Date()): void {
  const rows = users.map(toRow);
  const csv = [
    HEADERS.join(','),
    ...rows.map((r) => r.map(csvCell).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `users-export-${today.toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
